/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';

import { Box, CircularProgress, InputAdornment, InputBase, alpha } from '@mui/material';
import { useTheme } from '../../styles';

interface PopperComponentProps {
  anchorEl?: any;
  disablePortal?: boolean;
  open: boolean;
}

const StyledAutocompletePopper = styled.div`
  & .${autocompleteClasses.paper} {
    box-shadow: none;
    margin: 0;
    color: white;
    border-radius: 8px;
    font-size: 13px;
    border: 0 !important;
  }

  & .${autocompleteClasses.groupLabel} {
    background-color: ${({ theme }) => theme.background.standard};
    color: ${({ theme }) => theme.text.subtext};
    font-size: 12px;
    height: 38px;
    font-weight: 00;
  }

  & .${autocompleteClasses.noOptions} {
    display: none;
  }

  & .${autocompleteClasses.listbox} {
    background-color: ${({ theme }) => theme.background.standard};
    border-radius: 8px;
    padding: 0;
    border: ${({ theme }) => `1px solid ${theme.line.light}`};

    & .${autocompleteClasses.option} {
      min-height: auto;
      align-items: flex-start;
      padding: ${({ theme }) => `${theme.spacing(1.5)} ${theme.spacing(2)}`};
      color: ${({ theme }) => theme.text.standard};

      &:not(:last-of-type) {
        border-bottom: ${({ theme }) => `1px solid ${theme.line.lighter}`};
      }

      &[aria-selected='true'] {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
      }

      &:hover {
        background-color: ${({ theme }) => alpha(theme.text.standard, 0.06)};
      }

      &.Mui-focused {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }

      &.Mui-focusVisible {
        background-color: ${({ theme }) => `${alpha(theme.text.standard, 0.06)} !important`};
      }
    }
  }

  &.${autocompleteClasses.popperDisablePortal} {
    position: relative;
  }
`;

function PopperComponent(props: PopperComponentProps) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { disablePortal, anchorEl, open, ...other } = props;
  return <StyledAutocompletePopper {...other} />;
}

const StyledInput = styled(InputBase)<{ open: boolean }>`
  width: 100%;
  border: 1px solid ${({ theme }) => theme.line.light};
  border-radius: 6px;
  padding: 0px 13px;
  background-color: ${({ theme }) => theme.background.standard};
  font-size: 14;
  height: 40px;
  border-color: ${({ theme, open }) => (open ? theme.primary : theme.line.light)};
  outline: ${({ theme, open }) => (open ? `1.5px solid ${theme.primary}` : 'none')};

  & .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.text.standard};
    font-weight: 400;
  }

  /* box-shadow: ${({ open }) => (open ? `0px 0px 0px 3px #0c2d6b}` : '0px 0px 0px 3px transparent')}; */
`;

const Container = styled('div')<{ open: boolean }>(({ open }) => ({
  '& .MuiPaper-root': { border: open ? '1px solid #2f3744' : 0 },
}));

interface Props {
  value: string;
  onChange: (event: React.SyntheticEvent<Element, Event>, newValue: any | null) => void;
  disabled?: boolean;
  loading?: boolean;
  labels: any[];
  placeholder?: string;
}

export const Dropdown: React.FC<Props> = ({ value, onChange, disabled, labels, placeholder, loading }) => {
  const autocompleteRef = useRef(null);
  const theme = useTheme();

  const [open, setOpen] = useState<boolean>(false);

  return (
    <Container open={open}>
      <Autocomplete
        value={labels.find((item) => item.name === value) || null}
        ref={autocompleteRef}
        onChange={(event, newValue) => {
          onChange(event, newValue);
          setOpen(false);
          (document?.activeElement as HTMLElement)?.blur();
        }}
        disabled={disabled}
        PopperComponent={PopperComponent}
        groupBy={(option) => option?.name.split('/')[0]}
        renderOption={(props, option) => (
          <li {...props}>
            <Box
              component='span'
              sx={{
                width: 12,
                height: 12,
                flexShrink: 0,
                borderRadius: '3px',
                mr: 2,
                mt: '4px',
              }}
              style={{ backgroundColor: option?.metadata?.color }}
            />
            <Box
              sx={{
                flexGrow: 1,
                '& span': {
                  color: '#8b949e',
                },
              }}
            >
              {option?.name}
              <br />
              <span>{option?.description}</span>
            </Box>
          </li>
        )}
        autoHighlight
        options={labels}
        disablePortal
        getOptionLabel={(option) => option?.name || ''}
        renderInput={(params) => (
          <StyledInput
            open={open}
            ref={params.InputProps.ref}
            inputProps={params.inputProps}
            placeholder={placeholder}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onBlur={() => setOpen(false)}
            endAdornment={loading ? <CircularProgress sx={{ color: theme.primary }} size={14} /> : null}
            startAdornment={
              value && (
                <InputAdornment position='start'>
                  <Box
                    component='span'
                    sx={{
                      width: 12,
                      height: 12,
                      flexShrink: 0,
                      borderRadius: '3px',
                      mr: 1,
                      ml: 0.31,
                      mt: '0px',
                    }}
                    style={{ backgroundColor: labels.find((label) => label?.name === value)?.color }}
                  />
                </InputAdornment>
              )
            }
          />
        )}
      />
    </Container>
  );
};
